import { z } from 'zod';

/**
 * Spice Bazaar rules that both sides need: the token
 * and card tables, the 90 stall cards, the 10 patrons, and the pure
 * helpers for paying, taking and scoring. The server engine is the authority;
 * the client uses the same helpers only to show what is affordable.
 *
 * Colour order everywhere: white, blue, green, red, black (then wild).
 */

export const BAZAAR_COLORS = ['white', 'blue', 'green', 'red', 'black'] as const;
export type BazaarColor = (typeof BAZAAR_COLORS)[number];
export const BAZAAR_TOKENS = [...BAZAAR_COLORS, 'wild'] as const;
export type BazaarToken = (typeof BAZAAR_TOKENS)[number];
export const bazaarColorSchema = z.enum(BAZAAR_COLORS);
export const bazaarTokenSchema = z.enum(BAZAAR_TOKENS);

export const BAZAAR_MIN_PLAYERS = 2;
export const BAZAAR_MAX_PLAYERS = 4;
/** Fame points that trigger the final round. */
export const BAZAAR_WIN_POINTS = 15;
/** Most tokens (wild included) a player may hold at the end of a turn. */
export const BAZAAR_TOKEN_LIMIT = 10;
export const BAZAAR_STASH_LIMIT = 3;
export const BAZAAR_WILD = 5;
export const BAZAAR_PATRON_POINTS = 3;
/** Face-up cards per level. */
export const BAZAAR_MARKET_SLOTS = 4;
/** Consecutive turns a player may let run out before they are knocked out. */
export const BAZAAR_MAX_IDLE_TURNS = 3;
/** Each turn: two minutes, shared by the action and pressing Ready. */
export const BAZAAR_TURN_SECONDS = 120;

/** Spice tokens of each colour in the bank: 4 for two players, 5 for three, 7 for four. */
export function bazaarSpicesPerColor(players: number): number {
  return players <= 2 ? 4 : players === 3 ? 5 : 7;
}

/** One more patron than there are players. */
export function bazaarPatronCount(players: number): number {
  return players + 1;
}

/** A count for each of white, blue, green, red, black (and wild when six long). */
export type BazaarCounts = number[];

export interface BazaarCard {
  /** Index in {@link BAZAAR_CARDS}; stable, so it can name the card in messages. */
  id: number;
  level: 1 | 2 | 3;
  /** The permanent spice discount the card gives. */
  bonus: BazaarColor;
  points: number;
  /** Tokens needed, per colour. */
  cost: BazaarCounts;
}

/** level, bonus colour index, points, then the cost in white, blue, green, red, black. */
const CARD_ROWS: ReadonlyArray<readonly [number, number, number, number, number, number, number, number]> = [
  // Level 1
  [1, 4, 0, 1, 1, 1, 1, 0],
  [1, 4, 0, 1, 2, 1, 1, 0],
  [1, 4, 0, 2, 2, 0, 1, 0],
  [1, 4, 0, 0, 0, 1, 3, 1],
  [1, 4, 0, 0, 0, 2, 1, 0],
  [1, 4, 0, 2, 0, 2, 0, 0],
  [1, 4, 0, 0, 0, 3, 0, 0],
  [1, 4, 1, 0, 4, 0, 0, 0],
  [1, 1, 0, 1, 0, 1, 1, 1],
  [1, 1, 0, 1, 0, 1, 2, 1],
  [1, 1, 0, 1, 0, 2, 2, 0],
  [1, 1, 0, 0, 1, 3, 1, 0],
  [1, 1, 0, 1, 0, 0, 0, 2],
  [1, 1, 0, 0, 0, 2, 0, 2],
  [1, 1, 0, 0, 0, 0, 0, 3],
  [1, 1, 1, 0, 0, 0, 4, 0],
  [1, 0, 0, 0, 1, 1, 1, 1],
  [1, 0, 0, 0, 1, 2, 1, 1],
  [1, 0, 0, 0, 2, 2, 0, 1],
  [1, 0, 0, 3, 1, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 2, 1],
  [1, 0, 0, 0, 2, 0, 0, 2],
  [1, 0, 0, 0, 3, 0, 0, 0],
  [1, 0, 1, 0, 0, 4, 0, 0],
  [1, 2, 0, 1, 1, 0, 1, 1],
  [1, 2, 0, 1, 1, 0, 1, 2],
  [1, 2, 0, 0, 1, 0, 2, 2],
  [1, 2, 0, 1, 3, 1, 0, 0],
  [1, 2, 0, 2, 1, 0, 0, 0],
  [1, 2, 0, 0, 2, 0, 2, 0],
  [1, 2, 0, 0, 0, 0, 3, 0],
  [1, 2, 1, 0, 0, 0, 0, 4],
  [1, 3, 0, 1, 1, 1, 0, 1],
  [1, 3, 0, 2, 1, 1, 0, 1],
  [1, 3, 0, 2, 0, 1, 0, 2],
  [1, 3, 0, 1, 0, 0, 1, 3],
  [1, 3, 0, 0, 2, 1, 0, 0],
  [1, 3, 0, 2, 0, 0, 2, 0],
  [1, 3, 0, 3, 0, 0, 0, 0],
  [1, 3, 1, 4, 0, 0, 0, 0],
  // Level 2
  [2, 4, 1, 3, 2, 2, 0, 0],
  [2, 4, 1, 3, 0, 3, 0, 2],
  [2, 4, 2, 0, 1, 4, 2, 0],
  [2, 4, 2, 0, 0, 5, 3, 0],
  [2, 4, 2, 5, 0, 0, 0, 0],
  [2, 4, 3, 0, 0, 0, 0, 6],
  [2, 1, 1, 0, 2, 2, 3, 0],
  [2, 1, 1, 0, 2, 3, 0, 3],
  [2, 1, 2, 5, 3, 0, 0, 0],
  [2, 1, 2, 2, 0, 0, 1, 4],
  [2, 1, 2, 0, 5, 0, 0, 0],
  [2, 1, 3, 0, 6, 0, 0, 0],
  [2, 0, 1, 0, 0, 3, 2, 2],
  [2, 0, 1, 2, 3, 0, 3, 0],
  [2, 0, 2, 0, 0, 1, 4, 2],
  [2, 0, 2, 0, 0, 0, 5, 3],
  [2, 0, 2, 0, 0, 0, 5, 0],
  [2, 0, 3, 6, 0, 0, 0, 0],
  [2, 2, 1, 3, 0, 2, 3, 0],
  [2, 2, 1, 2, 3, 0, 0, 2],
  [2, 2, 2, 4, 2, 0, 0, 1],
  [2, 2, 2, 0, 5, 3, 0, 0],
  [2, 2, 2, 0, 0, 5, 0, 0],
  [2, 2, 3, 0, 0, 6, 0, 0],
  [2, 3, 1, 2, 0, 0, 2, 3],
  [2, 3, 1, 0, 3, 0, 2, 3],
  [2, 3, 2, 1, 4, 2, 0, 0],
  [2, 3, 2, 3, 0, 0, 0, 5],
  [2, 3, 2, 0, 0, 0, 0, 5],
  [2, 3, 3, 0, 0, 0, 6, 0],
  // Level 3
  [3, 4, 3, 3, 3, 5, 3, 0],
  [3, 4, 4, 0, 0, 0, 7, 0],
  [3, 4, 4, 0, 0, 3, 6, 3],
  [3, 4, 5, 0, 0, 0, 7, 3],
  [3, 1, 3, 3, 0, 3, 3, 5],
  [3, 1, 4, 7, 0, 0, 0, 0],
  [3, 1, 4, 6, 3, 0, 0, 3],
  [3, 1, 5, 7, 3, 0, 0, 0],
  [3, 0, 3, 0, 3, 3, 5, 3],
  [3, 0, 4, 0, 0, 0, 0, 7],
  [3, 0, 4, 3, 0, 0, 3, 6],
  [3, 0, 5, 3, 0, 0, 0, 7],
  [3, 2, 3, 5, 3, 0, 3, 3],
  [3, 2, 4, 0, 7, 0, 0, 0],
  [3, 2, 4, 3, 6, 3, 0, 0],
  [3, 2, 5, 0, 7, 3, 0, 0],
  [3, 3, 3, 3, 5, 3, 0, 3],
  [3, 3, 4, 0, 0, 7, 0, 0],
  [3, 3, 4, 0, 3, 6, 3, 0],
  [3, 3, 5, 0, 0, 7, 3, 0],
];

export const BAZAAR_CARDS: readonly BazaarCard[] = CARD_ROWS.map(
  ([level, bonus, points, ...cost], id) => ({
    id,
    level: level as 1 | 2 | 3,
    bonus: BAZAAR_COLORS[bonus]!,
    points,
    cost,
  }),
);

export interface BazaarPatron {
  id: number;
  /** Bonuses (owned cards, not tokens) needed, per colour. */
  requires: BazaarCounts;
}

/** Five patrons want four cards of two colours; five want three cards of three. */
export const BAZAAR_PATRONS: readonly BazaarPatron[] = [
  [4, 4, 0, 0, 0],
  [0, 4, 4, 0, 0],
  [0, 0, 4, 4, 0],
  [0, 0, 0, 4, 4],
  [4, 0, 0, 0, 4],
  [3, 3, 3, 0, 0],
  [0, 3, 3, 3, 0],
  [0, 0, 3, 3, 3],
  [3, 0, 0, 3, 3],
  [3, 3, 0, 0, 3],
].map((requires, id) => ({ id, requires }));

export const BAZAAR_DECK_SIZES: readonly [number, number, number] = [40, 30, 20];

const sum = (counts: readonly number[]): number => counts.reduce((a, b) => a + b, 0);

/** Total tokens held (wild included). */
export const bazaarTokenTotal = (tokens: readonly number[]): number => sum(tokens);

/** Bonuses a set of owned cards gives, per colour. */
export function bazaarBonuses(cardIds: readonly number[]): BazaarCounts {
  const bonuses = [0, 0, 0, 0, 0];
  for (const id of cardIds) bonuses[BAZAAR_COLORS.indexOf(BAZAAR_CARDS[id]!.bonus)]! += 1;
  return bonuses;
}

/** Whether a set of bonuses satisfies a patron. */
export const bazaarPatronMet = (patron: BazaarPatron, bonuses: readonly number[]): boolean =>
  patron.requires.every((need, i) => (bonuses[i] ?? 0) >= need);

/**
 * How `tokens` (white, blue, green, red, black, wild) pay for `card` given the
 * player's bonuses, or null when they cannot. Bonuses come off the price
 * first; coloured tokens pay what is left of each colour and wild covers any
 * shortfall. Coloured tokens are always spent before wild, which is never
 * worse for the player (wild is the more flexible token).
 */
export function bazaarPayment(
  card: BazaarCard,
  tokens: readonly number[],
  bonuses: readonly number[],
): BazaarCounts | null {
  const pay = [0, 0, 0, 0, 0, 0];
  let wildNeeded = 0;
  for (let i = 0; i < 5; i += 1) {
    const owed = Math.max(0, card.cost[i]! - (bonuses[i] ?? 0));
    const coloured = Math.min(owed, tokens[i] ?? 0);
    pay[i] = coloured;
    wildNeeded += owed - coloured;
  }
  if (wildNeeded > (tokens[5] ?? 0)) return null;
  pay[5] = wildNeeded;
  return pay;
}

/**
 * Why `colors` is not a legal "take spices" action for this bank, or null when
 * it is. Legal: three different colours; two of one colour when at least four
 * are in the bank; or, when fewer than three colours have any tokens left,
 * one of each colour that does.
 */
export function bazaarTakeError(bank: readonly number[], colors: readonly BazaarColor[]): string | null {
  if (colors.length === 0) return 'Pick some spices to take';
  const indices = colors.map((c) => BAZAAR_COLORS.indexOf(c));
  const distinct = new Set(indices);
  if (colors.length === 2 && distinct.size === 1) {
    return (bank[indices[0]!] ?? 0) >= 4 ? null : 'You can only take two of a colour when four or more are left';
  }
  if (distinct.size !== colors.length || colors.length > 3) return 'Take three different colours, or two of one colour';
  if (indices.some((i) => (bank[i] ?? 0) < 1)) return 'That colour has run out';
  const available = BAZAAR_COLORS.filter((_, i) => (bank[i] ?? 0) > 0).length;
  if (colors.length < Math.min(3, available)) return 'Take three different colours';
  return null;
}
